import { z } from 'zod';

/** A single transfer within one gameweek's plan step. */
export const PlannedTransfer = z.object({
  out: z.string(),
  in: z.string(),
  /** Net cost in tenths of a million; negative means money freed up. */
  netCost: z.number(),
});
export type PlannedTransfer = z.infer<typeof PlannedTransfer>;

/** One gameweek of a multi-week transfer plan. */
export const PlanStep = z.object({
  gameweek: z.number().int(),
  transfers: z.array(PlannedTransfer),
  /** Free transfers in hand before this gameweek's moves. */
  freeTransfers: z.number().int(),
  /** Points deducted for transfers beyond the free allowance. */
  pointsHit: z.number().int(),
  /** Starting XI after the transfers, best legal formation for this gameweek. */
  xi: z.array(z.string()),
  captain: z.string(),
  /** Money left in the bank after this gameweek's moves, in tenths. */
  bank: z.number(),
  /** Projected points this gameweek, net of any hit. */
  projectedPoints: z.number(),
  /** Projected points versus holding the current squad through this gameweek. */
  pointsDelta: z.number(),
});
export type PlanStep = z.infer<typeof PlanStep>;

/** A multi-week transfer plan produced by the beam search in `planner.ts`. */
export const TransferPlan = z.object({
  horizon: z.number().int(),
  steps: z.array(PlanStep),
  /** Sum of the per-step deltas; zero when holding is the best plan. */
  totalPointsDelta: z.number(),
  /** Plans the bounded search actually scored. */
  plansEvaluated: z.number().int(),
  warnings: z.array(z.string()).default([]),
});
export type TransferPlan = z.infer<typeof TransferPlan>;

export const PlanRequest = z.object({
  /** How many upcoming gameweeks the plan should look ahead over. */
  horizon: z.number().int().min(1).max(8).default(3),
  /** Set true to let a step take a points hit for extra transfers. */
  allowHits: z.boolean().default(false),
});
export type PlanRequest = z.infer<typeof PlanRequest>;
